/**
 * Bluebird Co cart: free shipping bar and free gift.
 * Reads the JSON rendered by snippets/bb-cart-gift-config.liquid (re-rendered with every cart
 * refresh). Thresholds defined in the store currency are converted with Shopify's published rate
 * (window.Shopify.currency.rate), the same rate checkout uses, so a shopper paying in another
 * currency sees the bar and gets the gift at the right point. The gift line is added at the
 * threshold, removed below it, never alone in the cart, always quantity 1. Checkout is gated
 * while a change is in flight.
 */
(() => {
  const CONFIG_SELECTOR = '[data-bb-gift-config]';
  const BAR_SELECTOR = '.bb-cart-bar';
  const GATED_SELECTOR = '[data-bb-checkout], [data-bb-express]';
  const WATCHDOG_MS = 3000;
  const COOLDOWN_MS = 30000;

  let busy = false;
  let watchdog = null;
  let cooldownUntil = 0;

  const readConfig = () => {
    const el = document.querySelector(CONFIG_SELECTOR);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  };

  const currencyRate = (config) => {
    if (config.sameCurrency) return 1;
    const shopify = window.Shopify;
    const rate = shopify && shopify.currency && shopify.currency.active === config.currency
      ? Number(shopify.currency.rate) : 0;
    return rate > 0 ? rate : 0;
  };

  const formatMoney = (cents, format) => {
    const template = format || '${{amount}}';
    const match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';
    const amount = cents / 100;
    const build = (decimalMark, thousandsMark, withDecimals) => {
      const fixed = withDecimals ? amount.toFixed(2) : Math.round(amount).toString();
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsMark);
      return fraction === undefined ? grouped : grouped + decimalMark + fraction;
    };
    let value;
    switch (key) {
      case 'amount_no_decimals': value = build('.', ',', false); break;
      case 'amount_with_comma_separator': value = build(',', '.', true); break;
      case 'amount_no_decimals_with_comma_separator': value = build(',', '.', false); break;
      case 'amount_with_apostrophe_separator': value = build('.', "'", true); break;
      case 'amount_with_space_separator': value = build(',', ' ', true); break;
      case 'amount_no_decimals_with_space_separator': value = build(',', ' ', false); break;
      default: value = build('.', ',', true);
    }
    return template.replace(/\{\{\s*\w+\s*\}\}/, value);
  };

  /**
   * Works out both thresholds in the shopper's currency and repaints the bar.
   * Returns the gift threshold in the shopper's currency, or -1 when it cannot be worked out.
   */
  const renderBar = (config) => {
    const rate = currencyRate(config);
    if (!rate) return -1;
    const convert = (storeCents) => Math.round(storeCents * rate);
    const ship = config.shipNative || (config.shipStore ? convert(config.shipStore) : 0);
    const gift = config.enabled && config.giftStore ? convert(config.giftStore) : 0;

    const bar = document.querySelector(BAR_SELECTOR);
    if (!bar) return gift;

    let low = 0;
    let lowKind = '';
    let high = 0;
    let highKind = '';
    if (ship > 0 && gift > 0) {
      if (gift === ship) {
        high = ship; highKind = 'both';
      } else if (gift < ship) {
        low = gift; lowKind = 'gift'; high = ship; highKind = 'shipping';
      } else {
        low = ship; lowKind = 'shipping'; high = gift; highKind = 'gift';
      }
    } else if (ship > 0) {
      high = ship; highKind = 'shipping';
    } else if (gift > 0) {
      high = gift; highKind = 'gift';
    }
    if (!high) return gift;

    const total = config.total;
    const progress = Math.min(1, Math.max(0, total / high));
    let kind = 'met';
    let diff = 0;
    if (total < low) {
      diff = low - total; kind = lowKind;
    } else if (total < high) {
      diff = high - total; kind = highKind;
    }

    let message;
    if (kind === 'shipping' || kind === 'both') message = config.copy.shipping;
    else if (kind === 'gift') message = config.copy.gift;
    else if (low === 0 && highKind === 'shipping') message = config.copy.shippingOnly;
    else message = config.copy.unlocked;
    if (diff) {
      const money = formatMoney(diff, config.moneyFormat);
      message = message.split('[amount]').join(money).split('{{ amount }}').join(money);
    }

    bar.querySelector('.bb-cart-bar__message').innerHTML = message;
    bar.style.setProperty('--free-shipping-progress', progress >= 1 ? '1' : progress.toFixed(3));
    bar.style.setProperty('--free-shipping-bar-color', progress >= 1 ? config.colors.met : config.colors.under);
    bar.dataset.freeShippingProgress = progress >= 1 ? '1' : progress.toFixed(3);
    bar.dataset.freeShippingAmount = high;
    bar.dataset.bbLow = lowKind;
    bar.dataset.bbHigh = highKind;
    bar.classList.toggle('bb-cart-bar--tiered', low > 0);
    bar.classList.toggle('bb-cart-bar--low-met', low > 0 && total >= low);
    bar.classList.toggle('bb-cart-bar--high-met', progress >= 1);
    let tick = bar.querySelector('.bb-cart-bar__tick');
    if (low > 0) {
      if (!tick) {
        tick = document.createElement('span');
        tick.className = 'bb-cart-bar__tick';
        bar.querySelector('.free-shipping-bar__bar').appendChild(tick);
      }
      bar.style.setProperty('--bb-cart-tier', ((low / high) * 100).toFixed(1) + '%');
    } else if (tick) {
      tick.remove();
    }
    bar.classList.remove('bb-cart-bar--pending');
    return gift;
  };

  const gate = (locked) => {
    document.querySelectorAll(GATED_SELECTOR).forEach((el) => {
      if (locked) {
        el.setAttribute('aria-disabled', 'true');
        el.classList.add('is-loading');
      } else {
        el.removeAttribute('aria-disabled');
        el.classList.remove('is-loading');
      }
    });
    clearTimeout(watchdog);
    if (locked) {
      watchdog = setTimeout(() => {
        if (!busy) gate(false);
      }, WATCHDOG_MS);
    }
  };

  const post = (url, body) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  });

  const cartChanged = () => {
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
  };

  const run = async (request) => {
    busy = true;
    gate(true);
    let ok = false;
    try {
      const response = await request();
      ok = response.ok;
    } catch (error) {
      ok = false;
    }
    busy = false;
    if (ok) {
      cartChanged();
    } else {
      cooldownUntil = Date.now() + COOLDOWN_MS;
      gate(false);
    }
  };

  const updates = (changes) => post(theme.routes.cartUpdate, { updates: changes });

  const evaluate = () => {
    if (busy) return;
    const config = readConfig();
    if (!config) {
      gate(false);
      return;
    }

    const giftThreshold = renderBar(config);
    const qualifies = giftThreshold >= 0
      ? (giftThreshold > 0 && config.total >= giftThreshold)
      : config.qualifies;

    if (!config.enabled || !config.variantId || Date.now() < cooldownUntil) {
      gate(false);
      return;
    }

    const strays = (config.strayKeys || []).filter(Boolean);
    if (strays.length) {
      const changes = {};
      strays.forEach((key) => { changes[key] = 0; });
      run(() => updates(changes));
      return;
    }

    if (config.cartHasGift && config.giftQuantity !== 1) {
      run(() => updates({ [config.giftLineKey]: 1 }));
      return;
    }

    if (config.cartHasGift && (!qualifies || config.otherCount === 0)) {
      run(() => updates({ [config.giftLineKey]: 0 }));
      return;
    }

    if (!config.cartHasGift && qualifies && config.otherCount > 0) {
      run(() => post(theme.routes.cartAdd, {
        items: [{ id: config.variantId, quantity: 1, properties: { _auto_free_gift: 'true' } }]
      }));
      return;
    }

    gate(false);
  };

  document.addEventListener('on:cart:change', () => {
    if (!busy) gate(true);
  });
  document.addEventListener('on:cart:after-merge', evaluate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', evaluate);
  } else {
    evaluate();
  }
})();
